import cn from 'classnames';
import React, { FC, PropsWithChildren } from 'react';
import { PADDINGABLE_FOR_MOBILE_STATUS_BAR_CLASS } from 'students/constants';
import { SPage } from 'students/views/shared/styled/SPage';
import styled from 'styled-components';

interface IProps {
  solidColor?: string;
  gradient?: string;
  bgImage?: string;
  bgSize?: string;
  bgPosition?: string;
  hasImageOverlay?: boolean;
  overlayColor?: string;
  style?: React.CSSProperties;
  className?: string;
}

const PageWithBg: FC<PropsWithChildren<IProps>> = ({
  solidColor,
  gradient,
  bgImage,
  bgSize,
  bgPosition,
  hasImageOverlay,
  overlayColor,
  children,
  style,
  className
}) => {
  return (
    <SPage style={style} className={cn(className)}>
      {(gradient || solidColor || bgImage) && (
        <SBackgroundWrapper
          gradient={gradient || solidColor}
          bgImage={bgImage}
          bgSize={bgSize}
          bgPosition={bgPosition}
          hasImageOverlay={hasImageOverlay}
          overlayColor={overlayColor}
        />
      )}

      <SContentWrapper>{children}</SContentWrapper>
    </SPage>
  );
};

export default PageWithBg;

const SBackgroundWrapper = styled.div<{
  gradient?: string;
  bgImage?: string;
  bgSize?: string;
  bgPosition?: string;
  hasImageOverlay?: boolean;
  overlayColor?: string;
}>`
  position: fixed;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: ${({ gradient }) => gradient || ''};
  z-index: -1;

  ${({ hasImageOverlay, bgImage, gradient, bgSize, bgPosition, overlayColor }) =>
    hasImageOverlay && bgImage
      ? `
    background: ${bgImage}${gradient ? `, ${gradient}` : ''};
    background-size: ${bgSize || 'cover'};
    background-repeat: no-repeat;
    background-position: ${bgPosition || 'center'};

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: ${
        overlayColor
          ? `linear-gradient(${overlayColor}33, ${overlayColor}33), rgba(0, 0, 0, 0.6)`
          : 'rgba(0, 0, 0, 0.35)'
      };
      backdrop-filter: blur(6px);
    }
  `
      : ''}

  @media (min-width: ${({ theme }) => theme.linguBptMd}) {
    ${({ bgImage, gradient, hasImageOverlay }) =>
      bgImage && !hasImageOverlay
        ? // Guard the gradient the same way as the overlay branch above: an undefined
          // gradient makes the shorthand invalid and drops the declaration. Do NOT append
          // a colour layer either: every `colorsGradientMap` gradient already ends in a
          // colour, so an extra layer puts one in a non-final position — invalid per the
          // `background` grammar. And because the value contains `var()`, it is discarded
          // at computed-value time *while still winning the cascade*, so the base rule
          // above cannot apply either and the background vanishes instead of falling back.
          `background: ${bgImage}${gradient ? `, ${gradient}` : ''};`
        : ''};
    ${({ hasImageOverlay, bgSize }) =>
      !hasImageOverlay ? `background-size: ${bgSize || 'contain'};` : ''};
    ${({ hasImageOverlay }) =>
      !hasImageOverlay ? 'background-repeat: no-repeat, no-repeat;' : ''};
    ${({ bgPosition, hasImageOverlay }) =>
      !hasImageOverlay
        ? bgPosition
          ? `background-position: ${bgPosition};`
          : 'background-position-x: left, right;'
        : ''};
  }
`;

const SContentWrapper = styled.div.attrs((props) => ({
  className: cn(PADDINGABLE_FOR_MOBILE_STATUS_BAR_CLASS, props.className)
}))`
  top: 0;
  z-index: 1;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;
